import { baseApi } from "../app-main/store/baseApi";

/** One day's hourly rate for a listing, as stored by `spaces-v2`. */
type ListingDatePrice = {
  /** Listing-local date, `YYYY-MM-DD`. */
  date: string;
  hourly_rate: number;
  /** Start of the Peerspace Day (6am listing time), ISO instant. */
  start?: string;
  /** End of the Peerspace Day (6am the next day), ISO instant, exclusive. */
  end?: string;
};

type ListingDatePricesResponse = {
  listing_id: string;
  tzid: string;
  date_prices: ListingDatePrice[];
};

type SaveListingDatePricesArgs = {
  listingId: string;
  dates: Pick<ListingDatePrice, "date" | "hourly_rate">[];
};

type DeleteListingDatePricesArgs = {
  listingId: string;
  /** Listing-local dates to delete, `YYYY-MM-DD`. */
  dates: ListingDatePrice["date"][];
};

type DeleteListingDatePricesResponse = {
  listing_id: string;
  /** Dates that existed and were removed; missing dates aren't an error. */
  deleted_count: number;
};

const LISTING_DATE_PRICES_TAG = "ListingDatePrices";

/**
 * A listing's date based prices. Backed by
 * `GET` / `POST /v2/spaces-v2/listings/:listingId/calendar-pricing/dates`
 * and `POST .../calendar-pricing/dates/delete`: GET returns today's and later
 * dates; POST upserts one price per date; delete removes the given dates.
 */
const datePricesApi = baseApi
  .enhanceEndpoints({ addTagTypes: [LISTING_DATE_PRICES_TAG] })
  .injectEndpoints({
    endpoints: (builder) => ({
      getListingDatePrices: builder.query<
        ListingDatePricesResponse,
        { listingId: string }
      >({
        query: ({ listingId }) => ({
          url: `/v2/spaces-v2/listings/${listingId}/calendar-pricing/dates`,
        }),
        providesTags: (_result, _error, { listingId }) => [
          { type: LISTING_DATE_PRICES_TAG, id: listingId },
        ],
      }),
      saveListingDatePrices: builder.mutation<
        ListingDatePricesResponse,
        SaveListingDatePricesArgs
      >({
        query: ({ listingId, dates }) => ({
          url: `/v2/spaces-v2/listings/${listingId}/calendar-pricing/dates`,
          method: "POST",
          body: { dates },
        }),
        invalidatesTags: (_result, _error, { listingId }) => [
          { type: LISTING_DATE_PRICES_TAG, id: listingId },
        ],
      }),
      deleteListingDatePrices: builder.mutation<
        DeleteListingDatePricesResponse,
        DeleteListingDatePricesArgs
      >({
        query: ({ listingId, dates }) => ({
          url: `/v2/spaces-v2/listings/${listingId}/calendar-pricing/dates/delete`,
          method: "POST",
          body: { dates },
        }),
        invalidatesTags: (_result, _error, { listingId }) => [
          { type: LISTING_DATE_PRICES_TAG, id: listingId },
        ],
      }),
    }),
    overrideExisting: false,
  });

const {
  useDeleteListingDatePricesMutation,
  useGetListingDatePricesQuery,
  useSaveListingDatePricesMutation,
} = datePricesApi;

export {
  type DeleteListingDatePricesArgs,
  type DeleteListingDatePricesResponse,
  type ListingDatePrice,
  type ListingDatePricesResponse,
  type SaveListingDatePricesArgs,
  datePricesApi,
  useDeleteListingDatePricesMutation,
  useGetListingDatePricesQuery,
  useSaveListingDatePricesMutation,
};
